import React from 'react';
import { ArrowDownUp, Filter, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import './LegalPages.css';
import './SearchRankingHelp.css';

interface SearchRankingHelpProps {
  isLoggedIn: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
}

const SearchRankingHelp: React.FC<SearchRankingHelpProps> = ({
  isLoggedIn,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onLogout,
  onNavigateToProfile,
  onNavigateToMyAnnouncements,
  onNavigateToLocations,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
}) => {
  return (
    <div className="legal-page">
      <Header
        isLoggedIn={isLoggedIn}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <main className="legal-content">
        <BackButton className="legal-back-button" onClick={() => window.history.back()} />

        <article className="legal-container search-ranking-help-content">
          <header className="search-ranking-help-header">
            <span className="search-ranking-help-eyebrow">AJUTOR CĂUTARE</span>
            <h1 className="legal-title">Cum sunt ordonate rezultatele?</h1>
            <p>
              roade.ro afișează mai întâi anunțurile care corespund criteriilor tale,
              apoi le ordonează după opțiunea pe care ai ales-o.
            </p>
          </header>

          <ol className="search-ranking-help-flow" aria-label="Pașii folosiți pentru afișarea rezultatelor">
            <li><Search aria-hidden="true" /><span><strong>Căutare</strong>Titlul anunțului</span></li>
            <li><Filter aria-hidden="true" /><span><strong>Filtre</strong>Criteriile selectate</span></li>
            <li><ArrowDownUp aria-hidden="true" /><span><strong>Sortare</strong>Dată sau preț</span></li>
          </ol>

          <section className="legal-section search-ranking-help-section">
            <h2>Tipuri de anunțuri</h2>
            <p>Rezultatele sunt separate în două secțiuni:</p>
            <dl className="search-ranking-help-definitions">
              <div>
                <dt>Ce se vinde</dt>
                <dd>Oferte publicate de producători și alți vânzători.</dd>
              </div>
              <div>
                <dt>Ce se caută</dt>
                <dd>Cereri publicate de utilizatori care caută anumite produse.</dd>
              </div>
            </dl>
            <p className="search-ranking-help-note">
              Toate pozițiile sunt organice. roade.ro nu oferă în prezent poziționare contra cost,
              iar tipul anunțului nu îi acordă prioritate în listă.
            </p>
          </section>

          <section className="legal-section search-ranking-help-section">
            <h2>Cum sunt selectate rezultatele</h2>
            <p>
              Căutarea după text verifică titlul anunțului. Literele mari sau mici,
              diacriticele și folosirea unei cratime în locul unui spațiu nu schimbă rezultatul.
            </p>
            <p>
              Categoria, subcategoria, localitatea sau distanța, intervalul de preț,
              negociabilitatea, numele vânzătorului și tipul anunțului restrâng lista afișată.
              Aceste filtre stabilesc dacă un anunț apare, nu îi acordă un scor de relevanță.
            </p>
          </section>

          <section className="legal-section search-ranking-help-section">
            <h2>Tipuri de sortare</h2>
            <div className="search-ranking-help-sort-list">
              <div><h3>Cele mai noi</h3><p>Opțiunea implicită. Anunțurile cu data publicării cea mai recentă apar primele.</p></div>
              <div><h3>Cele mai vechi</h3><p>Anunțurile cu data publicării cea mai veche apar primele.</p></div>
              <div><h3>Preț crescător</h3><p>Anunțurile sunt afișate de la prețul cel mai mic la cel mai mare.</p></div>
              <div><h3>Preț descrescător</h3><p>Anunțurile sunt afișate de la prețul cel mai mare la cel mai mic.</p></div>
            </div>
          </section>

          <section className="legal-section search-ranking-help-section">
            <h2>Ce nu influențează ordinea</h2>
            <ul>
              <li>numărul de vizualizări, favorite sau evaluări;</li>
              <li>tipul vânzătorului, persoană fizică sau companie;</li>
              <li>istoricul tău de navigare sau comportamentul altor utilizatori;</li>
              <li>plăți pentru promovare sau un scor calculat prin învățare automată.</li>
            </ul>
          </section>

          <section className="legal-section search-ranking-help-section">
            <h2>Când nu există rezultate</h2>
            <p>
              roade.ro nu adaugă automat sinonime, nu schimbă categoria sau localitatea și nu
              completează lista cu anunțuri fără legătură. Poți modifica titlul căutat, elimina
              filtre sau mări distanța pentru a extinde rezultatele.
            </p>
          </section>
        </article>
      </main>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default SearchRankingHelp;
